import type { ProgramView } from '@shared/broadcast.ts'
import { formatJstClock } from '@shared/time.ts'
import type { PolicySlot } from '@shared/types.ts'
import { type Component, type JSX, Show } from 'solid-js'
import { OnAirBadge } from '~/components/OnAirBadge'
import { ViewerCount } from '~/components/ViewerCount'
import { slotRangeLabel, WEEKDAY_LABELS } from '~/lib/labels'

type Props = {
  live: boolean
  viewers: number
  viewersConnected: boolean
  program?: ProgramView
  slot?: PolicySlot
  commentsOpen: boolean
  onToggleComments: () => void
  onExit: () => void
  /** リアクションなど、帯に並べるもの */
  children?: JSX.Element
}

/**
 * 全画面のときに画面の下へ置く帯。
 *
 * 上下に分けず 1 本だけにしている。帯が増えるたびに映像の高さが削られるうえ、
 * リアクションや実況は手が届くところに無いと押されないため、下にまとめた。
 *
 * 映像には重ねない。重ねると YouTube のコントロールと当たって操作できなくなるうえ、
 * どちらの UI なのかも分かりにくくなるため、映像の外に高さを取っている（要件定義 3.3）。
 */
export const TheaterBar: Component<Props> = (props) => {
  const button =
    'shrink-0 rounded-full px-3 py-2 text-[11px] font-semibold transition-colors hover:bg-ch-border sm:py-1'

  return (
    <div class="flex h-14 shrink-0 items-center gap-3 border-t border-ch-border bg-ch-bg px-4 pb-[env(safe-area-inset-bottom)] sm:h-10">
      {/*
        全画面ではヘッダーとフッターを畳むので、非公式である表示はここが受け持つ
        （要件定義 14 章）。ヘッダーに合わせて短くしてある
      */}
      <p class="shrink-0 text-[11px] font-bold tracking-[0.12em]">
        834<span class="text-ch-accent">CH</span>
        <span class="ml-1.5 font-medium tracking-normal text-ch-dim">非公式</span>
      </p>

      {/* 狭い画面では畳む。この帯の幅は、まず全画面から戻る導線に使う */}
      <span class="hidden shrink-0 items-center gap-3 sm:flex">
        <OnAirBadge live={props.live} />
        <ViewerCount count={props.viewers} connected={props.viewersConnected} />
      </span>

      <Show when={props.program}>
        {(program) => (
          <p class="flex min-w-0 flex-1 items-baseline gap-2">
            <Show when={props.slot}>
              {(slot) => (
                <span class="hidden shrink-0 text-[11px] font-semibold tracking-[0.14em] text-ch-accent lg:inline">
                  {slot().label}
                  <span class="ml-1.5 font-mono font-normal tracking-normal text-ch-dim">
                    {WEEKDAY_LABELS[slot().day]}曜 {slotRangeLabel(slot())}
                  </span>
                </span>
              )}
            </Show>
            <span class="hidden shrink-0 font-mono text-[11px] text-ch-muted tabular sm:inline">
              {formatJstClock(Date.parse(program().startAt))} -{' '}
              {formatJstClock(Date.parse(program().endAt))}
            </span>
            <span class="truncate text-xs">{program().title}</span>
          </p>
        )}
      </Show>

      {/*
        全画面から戻れなくなるのが一番困るので、幅が足りないときに削られるのは
        リアクション側にする。実況と終了ボタンは shrink-0 のまま最後まで残す
      */}
      <span class="ml-auto flex min-w-0 items-center gap-1">
        <span class="flex min-w-0 shrink overflow-hidden">{props.children}</span>
        <button
          type="button"
          onClick={props.onToggleComments}
          aria-pressed={props.commentsOpen}
          class={button}
          classList={{
            'bg-ch-elevated text-ch-accent': props.commentsOpen,
            'text-ch-muted': !props.commentsOpen,
          }}
        >
          実況
        </button>
        <button
          type="button"
          onClick={props.onExit}
          aria-label="全画面を終了"
          class={`${button} text-ch-muted`}
        >
          ✕
        </button>
      </span>
    </div>
  )
}
